import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { colorVariables, votColorVariables } from '@gumtree/ui-library/src/base/theme';

export const ModernFooterBlock = styled.footer`
    background: #f7f7fa;
    color: #0c132d;
    padding: 24px 16px 32px;
`;

export const ModernFooterNav = styled.nav`
    margin: 0 auto;
`;

export const ModernFooterSectionCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
`;

export const ModernFooterDetails = styled.details`
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;

    &[open] .modern-footer-chevron {
        transform: rotate(225deg);
    }
`;

export const ModernFooterSummary = styled.summary`
    align-items: center;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    list-style: none;
    min-height: 48px;
    padding: 12px;

    &::-webkit-details-marker {
        display: none;
    }
`;

export const ModernFooterCardTitle = styled.span`
    color: #0c132d;
    font-size: 17px;
    font-weight: 600;
    line-height: 24px;
`;

export const ModernFooterChevron = styled.span`
    border: solid #0c132d;
    border-width: 0 2px 2px 0;
    display: inline-block;
    height: 8px;
    margin-right: 5px;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
    width: 8px;
`;

export const ModernFooterCardContent = styled.div`
    padding: 0 12px 12px;
`;

export const ModernFooterContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

export const ModernFooterGroup = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ModernFooterGroupTitle = styled.h4`
    color: #56575c;
    font-size: 17px;
    font-weight: 600;
    line-height: 24px;
    margin: 0;
`;

export const ModernFooterAppLinks = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ModernFooterSocialLinks = styled.div`
    align-items: center;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    max-width: 224px;
`;

export const ModernFooterSocialLink = styled.a`
    display: inline-flex;
    height: 24px;
    width: 24px;

    svg path {
        fill: #0c132d;
    }
`;

export const ModernFooterCopyright = styled.div`
    color: #81838d;
    font-size: 13px;
    letter-spacing: 0.01em;
    line-height: 20px;

    a {
        color: #1b73e8;
    }
`;

export const ModernFooterCopyrightText = styled.span`
    display: block;
`;

export const modernFooterTextLinkStyle = css`
    color: #0c132d;
    display: inline-flex;
    font-size: 13px;
    line-height: 20px;
    padding: 4px 0;
    text-decoration: underline;
`;

export const modernFooterCardLinkStyle = css`
    color: #1b73e8;
    display: block;
    font-size: 15px;
    line-height: 22px;
    padding: 6px 0;
    text-decoration: none;
`;

export const modernFooterBadgeLinkStyle = css`
    display: inline-flex;
    height: 41px;
    width: 140px;

    svg {
        height: 100%;
        width: 100%;
    }
`;

export const VotFooterBlock = styled.footer`
    background: ${votColorVariables.footerBackground};
    color: ${votColorVariables.neutralLight};
    font-family: 'Readex Pro', sans-serif;
    padding: 40px 16px 32px;

    @media (min-width: 48em) {
        padding: 24px 32px;
    }
`;

export const VotFooterNav = styled.nav`
    margin: 0 auto;
    max-width: 1122.645px;
`;

export const VotFooterPrimary = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;

    @media (min-width: 48em) {
        align-items: flex-start;
        flex-direction: row;
        justify-content: space-between;
    }
`;

export const VotFooterSectionCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;

    @media (min-width: 48em) {
        flex: 1;
        flex-direction: row;
        gap: 16px;
    }
`;

export const VotFooterDetails = styled.details`
    background: transparent;

    &[open] .vot-footer-chevron {
        transform: rotate(225deg);
    }

    ${ModernFooterCardContent} {
        padding: 0 4px 12px;
    }

    @media (min-width: 48em) {
        flex: 1;

        ${ModernFooterCardContent} {
            padding: 0;
        }
    }
`;

export const VotFooterSummary = styled.summary`
    align-items: center;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    list-style: none;
    min-height: 28px;
    padding: 12px 4px;

    &::-webkit-details-marker {
        display: none;
    }

    @media (min-width: 48em) {
        min-height: 22px;
        padding: 0 0 12px;
        pointer-events: none;
    }
`;

export const VotFooterTitle = styled.span`
    color: ${colorVariables.backgroundNeutral};
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
`;

export const VotFooterChevron = styled.span`
    border: solid ${votColorVariables.textMuted};
    border-width: 0 1.5px 1.5px 0;
    display: inline-block;
    height: 8px;
    margin-right: 5px;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
    width: 8px;

    @media (min-width: 48em) {
        display: none;
    }
`;

export const VotFooterApps = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (min-width: 48em) {
        flex: 0 0 165px;
    }
`;

export const VotFooterGroupTitle = styled.h4`
    color: ${votColorVariables.neutralLight};
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
    margin: 0;
`;

export const VotFooterAppLinks = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const VotFooterDesktopDivider = styled.div`
    display: none;

    @media (min-width: 48em) {
        background: ${votColorVariables.footerDivider};
        display: block;
        height: 2px;
        margin: 16px 0;
    }
`;

export const VotFooterSecondary = styled.div<{ hasPartners: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 28px;

    @media (min-width: 48em) {
        align-items: flex-start;
        flex-direction: row;
        justify-content: ${(props) => (props.hasPartners ? 'space-between' : 'center')};
    }
`;

export const VotFooterCommunity = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (min-width: 48em) {
        gap: 8px;
    }
`;

export const VotFooterSocialLinks = styled.div`
    align-items: center;
    border-bottom: 1px solid ${votColorVariables.footerBorder};
    border-top: 1px solid ${votColorVariables.footerBorder};
    display: flex;
    gap: 16px;
    justify-content: center;
    padding: 24px 0;

    @media (min-width: 48em) {
        border: 0;
        gap: 16px;
        justify-content: flex-start;
        padding: 0;
    }
`;

export const VotFooterSocialLink = styled.a`
    display: inline-flex;
    height: 24px;
    width: 24px;

    svg path {
        fill: ${colorVariables.backgroundNeutral};
    }
`;

export const VotFooterPartners = styled.section`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const VotFooterPartnerLink = styled.a`
    background: ${votColorVariables.textMuted};
    border-radius: 2px;
    color: ${colorVariables.backgroundNeutral};
    font-size: 14px;
    font-weight: 330;
    line-height: 22px;
    padding: 4px 8px;
    text-decoration: none;
`;

export const VotFooterCopyright = styled.div`
    color: ${votColorVariables.textMuted};
    font-size: 14px;
    font-weight: 330;
    letter-spacing: 0;
    line-height: 22px;
    margin-top: 32px;

    a {
        color: ${votColorVariables.footerLink};
        text-decoration: none;
    }

    @media (min-width: 48em) {
        margin: 34px auto 0;
        max-width: 695px;
        text-align: center;
    }
`;

export const votFooterTextLinkStyle = css`
    color: ${votColorVariables.footerLink};
    display: block;
    font-family: 'Readex Pro', sans-serif;
    font-size: 16px;
    font-weight: 330;
    line-height: 24px;
    padding: 2px 0;
    text-decoration: none;
`;

export const votFooterBadgeLinkStyle = css`
    display: inline-flex;
    height: 42px;
    width: 140px;

    svg {
        height: 100%;
        width: 100%;
    }
`;
